"use client";

import { type FC, type MouseEvent, type Ref, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Body,
  Button,
  Flex,
  HouseCard,
  type HouseCardProps,
  Icon,
  Modal,
  ProgressBar,
  type UIAction,
  useActions,
  usePubSub,
} from "@huub-web/ui";

import { useTemplates } from "../../hooks/useTemplates";
import type { TemplateCollection } from "../../types";
import { TemplatesParser } from "../../components/TemplatesParser";
import { usePageContent } from "../../hooks/usePageContent";

type HouseCardTemplatePropsType = {
  action?: UIAction;
  templates?: TemplateCollection;
  saved?: boolean;
  saveEndpoint?: string;
  deleteEndpoint?: string;
  elevated?: boolean;
  notListed?: boolean;
  record: HouseCardProps["record"];
  showSaleState?: HouseCardProps["showSaleState"];
  onClick?: HouseCardProps["onClick"];
};

export interface HouseCardTemplateProps extends HouseCardTemplatePropsType {}

/**
 * A template for a house/property card with house-specific features.
 */
export const HouseCardTemplate: FC<
  HouseCardTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  record,
  action,
  templates,
  saved: savedProp = false,
  deleteEndpoint,
  saveEndpoint,
  elevated = false,
  notListed = false,
  onClick,
  ref,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();
  const { $post, $delete, lockUI } = useTemplates();
  const pubSub = usePubSub();

  const [isConfirmationActive, setIsConfirmationActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(savedProp);

  const handleSaveToggle = () => {
    if (!saveEndpoint && !deleteEndpoint) {
      console.warn(
        "[HouseCardTemplate] No delete and/or save endpoint provided, skipping",
      );
      return;
    }

    if (!record.id) {
      console.error(
        "[HouseCardTemplate] Cannot update saved state: record.id is missing",
      );
      return;
    }

    const newState = !saved;

    if (newState) {
      if (!saveEndpoint) return;

      setLoading(true);

      $post(saveEndpoint, { id: record.id, saved: true })
        .then(() => {
          setSaved(true);
        })
        .catch((error) => {
          console.error("[HouseCardTemplate] Failed to save house", error);
          pubSub.publish("snackbar", {
            variant: "error",
            body: t("templating.property.saveError"),
          });
          setSaved(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      if (!deleteEndpoint) {
        console.warn(
          "[HouseCardTemplate] No delete endpoint provided, cannot delete",
        );
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteNotAllowed"),
        });
        return;
      }
      setIsConfirmationActive(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteEndpoint) {
      console.error("[HouseCardTemplate] Delete endpoint is missing");
      setIsConfirmationActive(false);
      return;
    }

    setLoading(true);

    // Construct the delete URL with the record ID
    $delete(deleteEndpoint)
      .then(() => {
        setSaved(false);
      })
      .catch((error) => {
        console.error("[HouseCardTemplate] Failed to delete house", error);
        pubSub.publish("snackbar", {
          variant: "error",
          body: t("templating.property.deleteError"),
        });
      })
      .finally(() => {
        setLoading(false);
        setIsConfirmationActive(false);
      });
  };

  const handleClick = (e: MouseEvent<HTMLElement>, id?: string) => {
    onClick?.(e, id);

    if (action) {
      handleAction(action);
    }
  };

  const houseContentItem = useMemo(() => {
    if (!record?.street) return null;
    const label = [record.street, record.city].filter(Boolean).join(", ");
    return {
      type: "data-item" as const,
      label,
      meta: {
        ...(record.price
          ? { price: `€${record.price.toLocaleString("nl-NL")}` }
          : {}),
        ...(record.energyLabel ? { energyLabel: record.energyLabel } : {}),
        ...(record.livingAreaSize
          ? { oppervlakte: `${record.livingAreaSize}m²` }
          : {}),
        ...(saved ? { opgeslagen: true } : {}),
      },
      ...(action ? { action } : {}),
    };
  }, [
    record?.street,
    record?.city,
    record?.price,
    record?.energyLabel,
    record?.livingAreaSize,
    saved,
    action,
  ]);
  usePageContent("housecard", houseContentItem);

  return (
    <>
      <HouseCard
        ref={ref}
        record={record}
        savable={(!!saveEndpoint && !saved) || (!!deleteEndpoint && saved)}
        saved={saved}
        onSaveToggle={handleSaveToggle}
        disabled={loading || isConfirmationActive || lockUI}
        onClick={action ? handleClick : undefined}
        border="default"
        elevated={elevated}
        notListed={notListed}
      >
        {templates && <TemplatesParser templates={templates} level={1} />}
      </HouseCard>

      {isConfirmationActive && (
        <Modal placement="center">
          {loading && <ProgressBar variant="infinite" position="absolute" />}

          <Body>
            {t("templating.property.deleteConfirmation", {
              street: record.street,
            })}
          </Body>
          <Flex justify="space-between" align="flex-end" gap="s">
            <Button
              rel="noopener noreferrer"
              variant="background"
              border="default"
              onClick={() => setIsConfirmationActive(false)}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              rel="noopener noreferrer"
              variant="error"
              trailing={<Icon icon="delete" size="s2" />}
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {t("delete")}
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};
